import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Clock, FacebookLogo, PaperPlaneTilt, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import api, { fmtErr, isAuPhone } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { document.title = "Contact Us | Zaekids Laundromat NSW"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.phone && !isAuPhone(form.phone)) {
      toast.error("Please enter a valid Australian phone number");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSending(false);
    }
  };

  const input = "w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm font-semibold outline-none transition-colors duration-200 focus:border-brand-green focus:ring-4 focus:ring-brand-green/10";

  return (
    <div data-testid="contact-page" className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <div className="text-center">
        <span className="font-script text-3xl font-bold text-brand-yellow-dark">Say Hello!</span>
        <h1 className="mt-2 text-4xl font-black tracking-tight text-brand-green sm:text-5xl">Contact Us</h1>
        <p className="mx-auto mt-4 max-w-xl text-slate-600">Questions about a booking, pricing or our service area? We'd love to hear from you.</p>
      </div>

      <div className="mt-14 grid gap-10 lg:grid-cols-2">
        <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl bg-white p-8 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
          <label className="mb-1 block text-sm font-extrabold text-brand-green">Your name *</label>
          <input data-testid="contact-name-input" className={input} required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" />
          <label className="mb-1 mt-4 block text-sm font-extrabold text-brand-green">Email *</label>
          <input data-testid="contact-email-input" type="email" className={input} required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@email.com" />
          <label className="mb-1 mt-4 block text-sm font-extrabold text-brand-green">Phone (optional)</label>
          <input data-testid="contact-phone-input" className={input} value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="04XX XXX XXX" />
          <label className="mb-1 mt-4 block text-sm font-extrabold text-brand-green">Message *</label>
          <textarea data-testid="contact-message-input" className={`${input} min-h-[120px]`} required value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?" />
          <button type="submit" disabled={sending} data-testid="contact-submit-btn"
            className="btn-bounce mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brand-green px-8 py-4 font-extrabold text-white disabled:opacity-60">
            <PaperPlaneTilt size={20} weight="duotone" /> {sending ? "Sending…" : "Send Message"}
          </button>
        </form>

        <div className="space-y-6">
          <div className="rounded-3xl bg-brand-green p-8 text-white">
            <p className="mt-4 flex items-start gap-3 text-green-100">
              <MapPin size={24} weight="duotone" className="shrink-0 text-brand-yellow" /> Located in Hebersham, NSW
            </p>
            <a href="mailto:Zaekidslaundromat@gmail.com" data-testid="contact-email-link"
              className="mt-4 flex items-center gap-3 break-all text-base font-extrabold hover:text-brand-yellow">
              <EnvelopeSimple size={24} weight="duotone" className="shrink-0 text-brand-yellow" /> Zaekidslaundromat@gmail.com
            </a>
            <a href="tel:+61406586239" data-testid="contact-phone-link"
              className="mt-4 flex items-center gap-3 text-base font-extrabold hover:text-brand-yellow">
              <Phone size={24} weight="duotone" className="shrink-0 text-brand-yellow" /> 0406 586 239
            </a>
            <div className="mt-4 flex items-start gap-3 text-green-100">
              <Clock size={24} weight="duotone" className="shrink-0 text-brand-yellow" />
              <span>24–48 hour turnaround<br />Pick up &amp; delivery daily: 5:00 PM – 7:00 PM</span>
            </div>
            <a href="https://www.facebook.com/search/top?q=Zaekids%20Laundromat" target="_blank" rel="noopener noreferrer" data-testid="contact-facebook-link"
              className="mt-4 flex items-center gap-3 text-green-100 hover:text-brand-yellow">
              <FacebookLogo size={24} weight="duotone" className="text-brand-yellow" /> Zaekids Laundromat
            </a>
          </div>
          <div className="overflow-hidden rounded-3xl shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
            <iframe title="Zaekids Laundromat map" data-testid="contact-map"
              src="https://www.google.com/maps?q=Hebersham%20NSW%202770%20Australia&output=embed"
              className="h-72 w-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </div>
    </div>
  );
}
